import { FeatureFlag } from '../../../../services/launchDarkly';
import { ISettingsSideNavSection } from '../../SideNavBar/settingsNavType';
import { QuoteAndContractSettingsMetadata } from './components/QuoteExperienceConfigSettings/quote-settings-experience-config.utils';

/** Single source of truth for every section key. Import instead of using raw strings. */
export const QUOTE_SETTING_KEYS = {
  // Top-level sections
  general: 'general',
  quoteDocumentTypes: 'quoteDocumentTypes',
  quoteDocuments: 'quoteDocuments',
  quoteEsignSettings: 'quoteEsignSettings',
  netTerms: 'netTerms',
  approval: 'approval',
  quoteProcessing: 'quoteProcessing',
  endAction: 'endAction',
  discountSettings: 'discountSettings',
  rateAndPriceSettings: 'rateAndPriceSettings',
  additionalSettings: 'additionalSettings',
  // Children of general
  quoteGeneralLength: 'quoteGeneralLength',
  quoteGeneralAddressSource: 'quoteGeneralAddressSource',
  quoteGeneralAllowPoNumber: 'quoteGeneralAllowPoNumber',
  quoteGeneralAllowVatNumber: 'quoteGeneralAllowVatNumber',
  quoteGeneralAllowAdjustingEsignToggle:
    'quoteGeneralAllowAdjustingEsignToggle',
  quoteGeneralOverridePricesDisplay: 'quoteGeneralOverridePricesDisplay',
  // Children of quoteEsignSettings
  quoteEsignDefaultContacts: 'quoteEsignDefaultContacts',
  quoteEsignSigningOrder: 'quoteEsignSigningOrder',
  // Children of approval
  quoteApprovalNotes: 'quoteApprovalNotes',
  // Children of quoteProcessing
  quoteProcessingBehavior: 'quoteProcessingBehavior',
  quoteProcessingStartDate: 'quoteProcessingStartDate',
  // Children of endAction
  renewalConfiguration: 'renewalConfiguration',
  // Children of discountSettings
  discountApplicationMethod: 'discountApplicationMethod',
} as const;

export type QuoteSettingSectionKey =
  | (typeof QUOTE_SETTING_KEYS)[keyof typeof QUOTE_SETTING_KEYS]
  | keyof typeof QuoteAndContractSettingsMetadata;

export interface QuoteSettingNode {
  key: QuoteSettingSectionKey;
  label: string;
  children?: QuoteSettingNode[];
  /** LaunchDarkly flag that gates this section or setting. Absent means always shown. */
  featureFlag?: keyof FeatureFlag;
}

const fromMeta = (
  key: keyof typeof QuoteAndContractSettingsMetadata,
  featureFlag?: keyof FeatureFlag,
): QuoteSettingNode => ({
  key,
  label: QuoteAndContractSettingsMetadata[key].label,
  featureFlag,
});

const isFeatureFlagEnabled = (
  { featureFlag }: QuoteSettingNode,
  featureFlags: FeatureFlag,
): boolean => !featureFlag || !!featureFlags[featureFlag];

export const QUOTE_SETTINGS_CONFIG: QuoteSettingNode[] = [
  {
    key: QUOTE_SETTING_KEYS.general,
    label: 'General',
    children: [
      { key: QUOTE_SETTING_KEYS.quoteGeneralLength, label: 'Length' },
      {
        key: QUOTE_SETTING_KEYS.quoteGeneralAddressSource,
        label: 'Address Source',
      },
      {
        key: QUOTE_SETTING_KEYS.quoteGeneralAllowPoNumber,
        label: 'Allow Purchase Order (PO) Number',
      },
      {
        key: QUOTE_SETTING_KEYS.quoteGeneralAllowVatNumber,
        label: 'Allow VAT Number',
      },
      {
        key: QUOTE_SETTING_KEYS.quoteGeneralAllowAdjustingEsignToggle,
        label: 'Allow Adjusting ESign Toggle During Quote Review',
      },
      {
        key: QUOTE_SETTING_KEYS.quoteGeneralOverridePricesDisplay,
        label: 'Override Prices Display on Quotes',
      },
    ],
  },
  {
    key: QUOTE_SETTING_KEYS.quoteDocumentTypes,
    label: 'Quote Document Types',
    featureFlag: 'enableQuoteDocumentManagement',
  },
  { key: QUOTE_SETTING_KEYS.quoteDocuments, label: 'Quote Documents' },
  {
    key: QUOTE_SETTING_KEYS.quoteEsignSettings,
    label: 'Quote eSign Settings',
    children: [
      {
        key: QUOTE_SETTING_KEYS.quoteEsignDefaultContacts,
        label: 'Default Signing Contacts',
      },
      {
        key: QUOTE_SETTING_KEYS.quoteEsignSigningOrder,
        label: 'Default Signing Order',
      },
    ],
  },
  { key: QUOTE_SETTING_KEYS.netTerms, label: 'Net Terms' },
  {
    key: QUOTE_SETTING_KEYS.approval,
    label: 'Approval',
    children: [
      fromMeta('allowRetainApprovalsRoles'),
      { key: QUOTE_SETTING_KEYS.quoteApprovalNotes, label: 'Approval Notes' },
    ],
  },
  {
    key: QUOTE_SETTING_KEYS.quoteProcessing,
    label: 'Quote Processing',
    children: [
      {
        key: QUOTE_SETTING_KEYS.quoteProcessingBehavior,
        label: 'Quote Processing Behavior',
      },
      { key: QUOTE_SETTING_KEYS.quoteProcessingStartDate, label: 'Start Date' },
    ],
  },
  {
    key: QUOTE_SETTING_KEYS.endAction,
    label: 'Renewal Action',
    children: [
      {
        key: QUOTE_SETTING_KEYS.renewalConfiguration,
        label: 'Renewal Configuration',
      },
    ],
  },
  {
    key: QUOTE_SETTING_KEYS.discountSettings,
    label: 'Discount Settings',
    featureFlag: 'enableDiscountApplicationMethodSettings',
    children: [
      {
        key: QUOTE_SETTING_KEYS.discountApplicationMethod,
        label: 'Discount Application Method',
      },
    ],
  },
  {
    key: QUOTE_SETTING_KEYS.rateAndPriceSettings,
    label: 'Rate and Price Settings',
    children: [
      fromMeta('accountSpecificRateCreation'),
      fromMeta('accountSpecificRateAllowTierAdjustments'),
      fromMeta('accountSpecificRateAllowPriceAdjustments'),
      fromMeta('accountSpecificRateAllowPercentageAdjustments'),
      fromMeta('lockPriceModelUpdateOnQuote'),
      fromMeta('allowQuantityDropdown'),
      fromMeta('enablePriceTierLockEligible'),
    ],
  },
  {
    key: QUOTE_SETTING_KEYS.additionalSettings,
    label: 'Additional Settings',
    children: [
      fromMeta('allowChangingQuoteOfferingStartAndEndDate'),
      fromMeta('enableEarlyRenewal'),
      fromMeta('enableQuoteItemNotes'),
      fromMeta('enableQuoteOfferingPriceTiers'),
      fromMeta('enableBulkAddQuoteOffering'),
      fromMeta('hideAutoRenewal'),
      fromMeta('hideBillingSchedule'),
      fromMeta('showCommaFormattedQty'),
      fromMeta('enableBillingAnchorAdjustment'),
      fromMeta('enableUpfrontBilling', 'showUpfrontBillingSetting'),
      fromMeta('quoteCreateAllowedRoles'),
      fromMeta('quoteNameEditableRoles'),
      fromMeta('quoteManualAcceptanceAllowedRoles'),
      fromMeta('quoteAcceptanceAllowedRoles'),
      fromMeta('showTOCPermission'),
    ],
  },
];

/**
 * Returns all section keys (the section itself plus any children) for a given
 * top-level section key. Pass the result directly to useSettingsSectionHighlights
 * instead of maintaining a hand-written string array in each component.
 */
export function getQuoteSettingsSectionKeys(
  sectionKey: QuoteSettingSectionKey,
): QuoteSettingSectionKey[] {
  const entry = QUOTE_SETTINGS_CONFIG.find((e) => e.key === sectionKey);
  if (!entry) {
    return [sectionKey];
  }
  const childKeys = (entry.children ?? []).map((c) => c.key);
  return [sectionKey, ...childKeys];
}

/**
 * Whether a section or child setting is enabled by the current feature flags.
 * Keys that are not in QUOTE_SETTINGS_CONFIG are always enabled.
 */
export function isQuoteSettingEnabled(
  key: QuoteSettingSectionKey,
  featureFlags: FeatureFlag,
): boolean {
  const node = QUOTE_SETTINGS_CONFIG.flatMap((section) => [
    section,
    ...(section.children ?? []),
  ]).find((entry) => entry.key === key);
  return !node || isFeatureFlagEnabled(node, featureFlags);
}

/**
 * Derives the ISettingsSideNavSection array for settingsNav.ts getSections,
 * filtered by the current feature flags.
 */
export function buildQuoteSettingsSections(
  featureFlags: FeatureFlag,
): ISettingsSideNavSection[] {
  return QUOTE_SETTINGS_CONFIG.filter((section) =>
    isFeatureFlagEnabled(section, featureFlags),
  ).map(({ key, label, children }) => ({
    key,
    label,
    children: children?.filter((child) =>
      isFeatureFlagEnabled(child, featureFlags),
    ),
  }));
}
